/** Canvas sprite library for the sign-in particle field.
 *
 *  Every ship/prop here is hand-drawn with 2D paths — no image assets, so the
 *  whole seasonal system costs nothing to load. Ported verbatim (geometry and
 *  colours) from the CinchAI auth page so the two products look identical.
 *
 *  Ship draws are origin-relative: the caller translates/rotates first, then
 *  calls draw*(size). Everything else takes explicit x/y.
 *
 *  .ts, not .tsx — these are artwork hex values, which the DS lint rule bans
 *  in .tsx and which have no semantic-token equivalent.
 */

type Ctx = CanvasRenderingContext2D;

export function drawEmoji(ctx: Ctx, emoji: string, x: number, y: number, size: number) {
  ctx.save();
  ctx.font = `${size}px serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(emoji, x, y);
  ctx.restore();
}

export function drawDeathStar(ctx: Ctx, x: number, y: number, size: number) {
  ctx.save();
  // Main sphere with radial gradient (lit from upper-left)
  const grad = ctx.createRadialGradient(x - size * 0.3, y - size * 0.3, size * 0.05, x, y, size);
  grad.addColorStop(0, "#aaaaaa");
  grad.addColorStop(0.5, "#666677");
  grad.addColorStop(1, "#222233");
  ctx.beginPath();
  ctx.arc(x, y, size, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  // Equatorial trench
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, size, 0, Math.PI * 2);
  ctx.clip();
  ctx.strokeStyle = "#444455";
  ctx.lineWidth = size * 0.18;
  ctx.beginPath();
  ctx.moveTo(x - size, y + size * 0.1);
  ctx.lineTo(x + size, y + size * 0.1);
  ctx.stroke();
  ctx.restore();
  // Superlaser focus dish (upper-left quadrant)
  const dishGrad = ctx.createRadialGradient(
    x - size * 0.28,
    y - size * 0.22,
    0,
    x - size * 0.28,
    y - size * 0.22,
    size * 0.32,
  );
  dishGrad.addColorStop(0, "#bbbbcc");
  dishGrad.addColorStop(1, "#556677");
  ctx.beginPath();
  ctx.arc(x - size * 0.28, y - size * 0.22, size * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = dishGrad;
  ctx.fill();
  ctx.restore();
}

/** Top-down: nose points +x, four wings spread diagonally forming an X. */
export function drawXWing(ctx: Ctx, size: number) {
  ctx.save();
  // ── Fuselage (long tapered body along x-axis) ──
  ctx.fillStyle = "#C8C8CC";
  ctx.beginPath();
  ctx.moveTo(size * 1.5, 0); // nose tip
  ctx.lineTo(size * 0.8, size * 0.13); // forward fuselage right
  ctx.lineTo(-size * 1.0, size * 0.18); // rear fuselage right
  ctx.lineTo(-size * 1.25, 0); // tail tip
  ctx.lineTo(-size * 1.0, -size * 0.18); // rear fuselage left
  ctx.lineTo(size * 0.8, -size * 0.13); // forward fuselage left
  ctx.closePath();
  ctx.fill();

  // ── 4 wings — spread diagonally from fuselage centre ──
  ctx.fillStyle = "#AAAAAF";
  const wings: [number, number][][] = [
    // forward-top
    [
      [size * 0.4, -size * 0.12],
      [size * 0.9, -size * 0.9],
      [size * 0.2, -size * 0.7],
      [-size * 0.1, -size * 0.15],
    ],
    // rear-top
    [
      [-size * 0.1, -size * 0.15],
      [size * 0.2, -size * 0.7],
      [-size * 0.4, -size * 0.9],
      [-size * 0.8, -size * 0.2],
    ],
    // forward-bottom
    [
      [size * 0.4, size * 0.12],
      [size * 0.9, size * 0.9],
      [size * 0.2, size * 0.7],
      [-size * 0.1, size * 0.15],
    ],
    // rear-bottom
    [
      [-size * 0.1, size * 0.15],
      [size * 0.2, size * 0.7],
      [-size * 0.4, size * 0.9],
      [-size * 0.8, size * 0.2],
    ],
  ];
  for (const wing of wings) {
    ctx.beginPath();
    wing.forEach(([wx, wy], wi) => (wi === 0 ? ctx.moveTo(wx, wy) : ctx.lineTo(wx, wy)));
    ctx.closePath();
    ctx.fill();
  }

  // ── Red markings on wings ──
  ctx.strokeStyle = "rgba(210, 35, 35, 0.9)";
  ctx.lineWidth = Math.max(0.5, size * 0.1);
  ctx.beginPath();
  ctx.moveTo(size * 0.55, -size * 0.48);
  ctx.lineTo(size * 0.72, -size * 0.72);
  ctx.moveTo(-size * 0.08, -size * 0.48);
  ctx.lineTo(-size * 0.24, -size * 0.72);
  ctx.moveTo(size * 0.55, size * 0.48);
  ctx.lineTo(size * 0.72, size * 0.72);
  ctx.moveTo(-size * 0.08, size * 0.48);
  ctx.lineTo(-size * 0.24, size * 0.72);
  ctx.stroke();

  // ── Engine nacelles at wing tips ──
  ctx.fillStyle = "#888890";
  const nacelles: [number, number][] = [
    [size * 0.9, -size * 0.9],
    [-size * 0.4, -size * 0.9],
    [size * 0.9, size * 0.9],
    [-size * 0.4, size * 0.9],
  ];
  for (const [nx, ny] of nacelles) {
    ctx.beginPath();
    ctx.arc(nx, ny, size * 0.12, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Engine glow at each nacelle ──
  ctx.fillStyle = "rgba(255, 140, 50, 0.9)";
  for (const [nx, ny] of nacelles) {
    ctx.beginPath();
    ctx.arc(nx - size * 0.08, ny, size * 0.07, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Cockpit canopy (small oval near nose) ──
  ctx.fillStyle = "rgba(100, 180, 255, 0.85)";
  ctx.beginPath();
  ctx.ellipse(size * 0.55, 0, size * 0.18, size * 0.09, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

export function drawTIEFighter(ctx: Ctx, size: number) {
  // Hexagonal solar panels
  ctx.fillStyle = "#3a4f62";
  ctx.strokeStyle = "#6a8faa";
  ctx.lineWidth = 0.6;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(side * size * 0.62, -size * 0.88);
    ctx.lineTo(side * size * 1.05, -size * 0.55);
    ctx.lineTo(side * size * 1.05, size * 0.55);
    ctx.lineTo(side * size * 0.62, size * 0.88);
    ctx.lineTo(side * size * 0.38, size * 0.7);
    ctx.lineTo(side * size * 0.38, -size * 0.7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // Panel grid lines
    ctx.strokeStyle = "#223344";
    ctx.lineWidth = 0.4;
    const px = side * size * 0.72;
    ctx.beginPath();
    ctx.moveTo(px, -size * 0.75);
    ctx.lineTo(px, size * 0.75);
    ctx.moveTo(side * size * 0.45, -size * 0.4);
    ctx.lineTo(side * size * 0.45, size * 0.4);
    ctx.stroke();
    ctx.strokeStyle = "#6a8faa";
    ctx.lineWidth = 0.6;
  }
  // Struts
  ctx.strokeStyle = "#99AABC";
  ctx.lineWidth = size * 0.09;
  ctx.beginPath();
  ctx.moveTo(-size * 0.38, 0);
  ctx.lineTo(-size * 0.28, 0);
  ctx.moveTo(size * 0.38, 0);
  ctx.lineTo(size * 0.28, 0);
  ctx.stroke();
  // Central pod
  const podGrad = ctx.createRadialGradient(-size * 0.1, -size * 0.1, 0.5, 0, 0, size * 0.3);
  podGrad.addColorStop(0, "#99AABB");
  podGrad.addColorStop(1, "#1a2a3a");
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.3, 0, Math.PI * 2);
  ctx.fillStyle = podGrad;
  ctx.fill();
  // Cockpit window
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.13, 0, Math.PI * 2);
  ctx.fillStyle = "#060a14";
  ctx.fill();
}

export function drawAWing(ctx: Ctx, size: number) {
  ctx.save();
  // Slim red fuselage
  ctx.fillStyle = "#CC2200";
  ctx.beginPath();
  ctx.moveTo(size * 1.4, 0);
  ctx.lineTo(size * 0.6, size * 0.1);
  ctx.lineTo(-size * 1.0, size * 0.14);
  ctx.lineTo(-size * 1.2, 0);
  ctx.lineTo(-size * 1.0, -size * 0.14);
  ctx.lineTo(size * 0.6, -size * 0.1);
  ctx.closePath();
  ctx.fill();
  // Swept delta wings
  ctx.fillStyle = "#AA1A00";
  for (const sy of [1, -1]) {
    ctx.beginPath();
    ctx.moveTo(size * 0.5, sy * size * 0.1);
    ctx.lineTo(size * 0.2, sy * size * 0.72);
    ctx.lineTo(-size * 0.7, sy * size * 0.62);
    ctx.lineTo(-size * 0.9, sy * size * 0.14);
    ctx.closePath();
    ctx.fill();
  }
  // White fuselage stripe
  ctx.fillStyle = "#DDCCCC";
  ctx.beginPath();
  ctx.rect(-size * 0.3, -size * 0.04, size * 0.9, size * 0.08);
  ctx.fill();
  // Dual engine nozzles
  ctx.fillStyle = "#555566";
  for (const sy of [1, -1]) {
    ctx.beginPath();
    ctx.arc(-size * 1.06, sy * size * 0.08, size * 0.09, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(255,150,50,0.9)";
  for (const sy of [1, -1]) {
    ctx.beginPath();
    ctx.arc(-size * 1.13, sy * size * 0.08, size * 0.05, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export function drawYWing(ctx: Ctx, size: number) {
  ctx.save();
  // Two engine nacelles flanking fuselage
  for (const sy of [-1, 1]) {
    const ny = sy * size * 0.52;
    ctx.fillStyle = "#888899";
    ctx.beginPath();
    ctx.moveTo(size, ny - size * 0.09);
    ctx.lineTo(size, ny + size * 0.09);
    ctx.lineTo(-size * 0.9, ny + size * 0.09);
    ctx.lineTo(-size * 0.9, ny - size * 0.09);
    ctx.closePath();
    ctx.fill();
    // Strut
    ctx.strokeStyle = "#666655";
    ctx.lineWidth = size * 0.07;
    ctx.beginPath();
    ctx.moveTo(-size * 0.08, sy > 0 ? size * 0.12 : -size * 0.12);
    ctx.lineTo(-size * 0.08, sy > 0 ? ny - size * 0.09 : ny + size * 0.09);
    ctx.stroke();
    // Engine glow
    ctx.fillStyle = "rgba(100,160,255,0.9)";
    ctx.beginPath();
    ctx.arc(-size * 0.9, ny, size * 0.07, 0, Math.PI * 2);
    ctx.fill();
  }
  // Central fuselage
  ctx.fillStyle = "#BBAA55";
  ctx.beginPath();
  ctx.moveTo(size * 1.5, 0);
  ctx.lineTo(size * 0.9, size * 0.12);
  ctx.lineTo(-size * 0.6, size * 0.12);
  ctx.lineTo(-size * 0.8, 0);
  ctx.lineTo(-size * 0.6, -size * 0.12);
  ctx.lineTo(size * 0.9, -size * 0.12);
  ctx.closePath();
  ctx.fill();
  // Exposed skeleton section
  ctx.fillStyle = "#776644";
  ctx.beginPath();
  ctx.rect(-size * 0.2, -size * 0.12, size * 0.6, size * 0.24);
  ctx.fill();
  // Ion cannon turret
  ctx.fillStyle = "#555566";
  ctx.beginPath();
  ctx.arc(size * 0.35, 0, size * 0.09, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Top-down: nose points +x. Mandibles forward, cockpit pod offset port (-y). */
export function drawMillenniumFalcon(ctx: Ctx, size: number) {
  ctx.save();
  // Main disc hull
  const hullGrad = ctx.createRadialGradient(size * 0.1, -size * 0.1, size * 0.05, 0, 0, size * 1.0);
  hullGrad.addColorStop(0, "#CCCCBB");
  hullGrad.addColorStop(1, "#888877");
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 1.0, size * 0.82, 0, 0, Math.PI * 2);
  ctx.fillStyle = hullGrad;
  ctx.fill();
  // Inner detail ring
  ctx.strokeStyle = "#777766";
  ctx.lineWidth = size * 0.06;
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.63, size * 0.52, 0, 0, Math.PI * 2);
  ctx.stroke();
  // Forward mandibles (two prongs right)
  ctx.fillStyle = "#AAAAAA";
  for (const sy of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(size * 0.35, sy * size * 0.32);
    ctx.lineTo(size * 1.32, sy * size * 0.2);
    ctx.lineTo(size * 1.28, 0);
    ctx.lineTo(size * 0.45, sy * size * 0.08);
    ctx.closePath();
    ctx.fill();
  }
  // Cockpit pod (port side = negative y)
  ctx.fillStyle = "#999988";
  ctx.beginPath();
  ctx.ellipse(-size * 0.08, -size * 0.85, size * 0.24, size * 0.12, 0.25, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(120,210,255,0.9)";
  ctx.beginPath();
  ctx.ellipse(-size * 0.08, -size * 0.85, size * 0.13, size * 0.06, 0.25, 0, Math.PI * 2);
  ctx.fill();
  // Radar dish
  ctx.fillStyle = "#CCCCAA";
  ctx.beginPath();
  ctx.arc(size * 0.18, size * 0.18, size * 0.17, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#888877";
  ctx.lineWidth = size * 0.025;
  ctx.beginPath();
  ctx.moveTo(size * 0.18, size * 0.01);
  ctx.lineTo(size * 0.18, size * 0.35);
  ctx.moveTo(size * 0.01, size * 0.18);
  ctx.lineTo(size * 0.35, size * 0.18);
  ctx.stroke();
  // Three engine ports (rear, -x side)
  for (const ey of [-size * 0.22, 0, size * 0.22]) {
    ctx.fillStyle = "#444455";
    ctx.beginPath();
    ctx.arc(-size * 0.9, ey, size * 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(100,180,255,0.85)";
    ctx.beginPath();
    ctx.arc(-size * 0.94, ey, size * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Darth Vader's TIE Advanced x1 — angular bent panels, red targeting eye. */
export function drawVaderTIE(ctx: Ctx, size: number) {
  ctx.save();
  for (const side of [-1, 1]) {
    // Bent panels, upper then lower
    for (const sy of [-1, 1]) {
      ctx.fillStyle = "#1a2a3a";
      ctx.strokeStyle = "#3a5a7a";
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(side * size * 0.34, sy * size * 0.58);
      ctx.lineTo(side * size * 0.92, sy * size * 1.08);
      ctx.lineTo(side * size * 1.12, sy * size * 0.52);
      ctx.lineTo(side * size * 0.37, sy * size * 0.18);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    // Panel grid lines
    ctx.strokeStyle = "#112233";
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.moveTo(side * size * 0.65, -size * 0.83);
    ctx.lineTo(side * size * 0.74, -size * 0.35);
    ctx.moveTo(side * size * 0.65, size * 0.83);
    ctx.lineTo(side * size * 0.74, size * 0.35);
    ctx.stroke();
    // Strut
    ctx.strokeStyle = "#7799BB";
    ctx.lineWidth = size * 0.09;
    ctx.beginPath();
    ctx.moveTo(side * size * 0.37, 0);
    ctx.lineTo(side * size * 0.28, 0);
    ctx.stroke();
  }
  // Central pod (slightly elongated vs regular TIE)
  const podGrad = ctx.createRadialGradient(-size * 0.1, -size * 0.1, 0.3, 0, 0, size * 0.32);
  podGrad.addColorStop(0, "#8899AA");
  podGrad.addColorStop(1, "#080f18");
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.33, size * 0.27, 0, 0, Math.PI * 2);
  ctx.fillStyle = podGrad;
  ctx.fill();
  // Dark cockpit ring
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.14, 0, Math.PI * 2);
  ctx.fillStyle = "#1a0000";
  ctx.fill();
  // Red targeting eye
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.08, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(220,0,0,0.95)";
  ctx.fill();
  // Red glow
  const redGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.22);
  redGlow.addColorStop(0, "rgba(255,0,0,0.35)");
  redGlow.addColorStop(1, "rgba(255,0,0,0)");
  ctx.beginPath();
  ctx.arc(0, 0, size * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = redGlow;
  ctx.fill();
  ctx.restore();
}

/** Cinch product tile — rounded square, drop shadow, white asterisk mark. */
export function drawCinchTile(ctx: Ctx, x: number, y: number, size: number, color: string) {
  const s = size * 2.4; // square tile
  const r = s * 0.18; // corner radius
  const ox = x - s / 2;
  const oy = y - s / 2;
  ctx.save();
  // Drop shadow
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = s * 0.22;
  ctx.shadowOffsetY = s * 0.08;
  // Solid coloured background
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(ox + r, oy);
  ctx.lineTo(ox + s - r, oy);
  ctx.arc(ox + s - r, oy + r, r, -Math.PI / 2, 0);
  ctx.lineTo(ox + s, oy + s - r);
  ctx.arc(ox + s - r, oy + s - r, r, 0, Math.PI / 2);
  ctx.lineTo(ox + r, oy + s);
  ctx.arc(ox + r, oy + s - r, r, Math.PI / 2, Math.PI);
  ctx.lineTo(ox, oy + r);
  ctx.arc(ox + r, oy + r, r, Math.PI, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  // White icon circle centred
  const iconR = s * 0.28;
  ctx.beginPath();
  ctx.arc(x, y, iconR, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.fill();
  // White asterisk (6-spoke) inside circle
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = s * 0.055;
  ctx.lineCap = "round";
  for (let a = 0; a < 3; a++) {
    const ang = (a * Math.PI) / 3;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(ang) * iconR * 0.6, y + Math.sin(ang) * iconR * 0.6);
    ctx.lineTo(x - Math.cos(ang) * iconR * 0.6, y - Math.sin(ang) * iconR * 0.6);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawSnowflake(
  ctx: Ctx,
  x: number,
  y: number,
  size: number,
  color: string,
  rotation: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  for (let arm = 0; arm < 6; arm++) {
    ctx.rotate(Math.PI / 3);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size, 0);
    ctx.moveTo(size * 0.35, 0);
    ctx.lineTo(size * 0.5, -size * 0.22);
    ctx.moveTo(size * 0.35, 0);
    ctx.lineTo(size * 0.5, size * 0.22);
    ctx.moveTo(size * 0.6, 0);
    ctx.lineTo(size * 0.75, -size * 0.18);
    ctx.moveTo(size * 0.6, 0);
    ctx.lineTo(size * 0.75, size * 0.18);
    ctx.stroke();
  }
  ctx.restore();
}

/** Mini American flag: 3:2, seven stripes + canton with a 3x2 star grid. */
export function drawFlag(ctx: Ctx, x: number, y: number, size: number) {
  const w = size * 3;
  const h = size * 2;
  const ox = x - w / 2;
  const oy = y - h / 2;
  const stripeH = h / 7;
  for (let s = 0; s < 7; s++) {
    ctx.fillStyle = s % 2 === 0 ? "#BF0A30" : "#FFFFFF";
    ctx.fillRect(ox, oy + s * stripeH, w, stripeH);
  }
  const cantonW = w * 0.4;
  const cantonH = stripeH * 4;
  ctx.fillStyle = "#002868";
  ctx.fillRect(ox, oy, cantonW, cantonH);
  const starCols = 3;
  const starRows = 2;
  const starSize = Math.max(cantonH / (starRows * 3), 1.5);
  for (let row = 0; row < starRows; row++) {
    for (let col = 0; col < starCols; col++) {
      const sx = ox + (cantonW * (col + 0.5)) / starCols;
      const sy = oy + (cantonH * (row + 0.5)) / starRows;
      drawStar(ctx, sx, sy, starSize, 0.4);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
    }
  }
}

/** Traces a five-pointed star path at x/y. Caller sets fillStyle + fills. */
export function drawStar(ctx: Ctx, x: number, y: number, size: number, innerRatio: number) {
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const outer = (i * Math.PI * 2) / 5 - Math.PI / 2;
    const inner = outer + Math.PI / 5;
    if (i === 0) ctx.moveTo(x + Math.cos(outer) * size, y + Math.sin(outer) * size);
    else ctx.lineTo(x + Math.cos(outer) * size, y + Math.sin(outer) * size);
    ctx.lineTo(x + Math.cos(inner) * size * innerRatio, y + Math.sin(inner) * size * innerRatio);
  }
  ctx.closePath();
}

/** Connection line with an arrowhead at the target end. */
export function drawArrowLine(
  ctx: Ctx,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  alpha: number,
  colorBase = "rgba(255,80,130,",
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const angle = Math.atan2(dy, dx);
  const aSize = 7;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = `${colorBase}${alpha})`;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - aSize * Math.cos(angle - Math.PI / 6), y2 - aSize * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - aSize * Math.cos(angle + Math.PI / 6), y2 - aSize * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fillStyle = `${colorBase}${alpha})`;
  ctx.fill();
}
